import { formatMoney, ownPeriodSpend } from '@/features/workspace-usage';
import type {
  PeriodBounds,
  WorkspaceLimitRow,
} from '@/lib/api/generated/model';

import { wouldStop } from './blockedForecast';

/**
 * What removing a workspace's custom spend limit does, in one sentence (spec 074 US2,
 * re-homed for spec 117).
 *
 * Three consequences, and they are not variations of one sentence: falling back to a
 * default the workspace has already outspent stops it; falling back to one it is under
 * does not; and falling back to *no* default makes it unlimited — the opposite outcome,
 * which must not read as the stopping case with a different number in it. Pure, so the
 * sheet can state it inline and the dialog that used to state it says the same words.
 *
 * The spend it names is the one the stop is decided by: the default's own period's
 * ("$3.00 today" for a Daily default read in October), else the period's (spec 237).
 */
export function overrideRemovalConsequence(
  row: Pick<
    WorkspaceLimitRow,
    'spend' | 'default_consumed_amount' | 'default_period'
  >,
  defaultLimit: string | null,
  currency: string,
  readPeriod: PeriodBounds,
): string {
  const periodSpent = `${formatMoney(row.spend, currency)} this period`;

  if (defaultLimit === null) {
    return `No organization default is set, so this workspace will be able to spend without limit. It has spent ${periodSpent}.`;
  }

  const fallback = formatMoney(defaultLimit, currency);
  const own = ownPeriodSpend(
    row.default_period,
    readPeriod,
    row.default_consumed_amount,
  );
  const spent = own
    ? `${formatMoney(own.spend, currency)} ${own.phrase}`
    : periodSpent;

  if (wouldStop(row, defaultLimit)) {
    return `It falls back to the organization default of ${fallback} — and stops immediately, because it has already spent ${spent}. It will run again when the limit is raised or the period turns over.`;
  }

  return `It falls back to the organization default of ${fallback} and keeps running. It has spent ${spent}.`;
}
